import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaBars, FaTimes, FaWhatsapp } from 'react-icons/fa';
import { NAV_LINKS } from '../../utils/constants';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 30);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? 'hidden' : 'unset';
  }, [isMobileMenuOpen]);

  const isActive = (path) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  return (
    <>
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        className={`sticky top-0 z-50 transition-all duration-400 ${
          isScrolled
            ? 'bg-white/95 shadow-card py-1.5'
            : 'bg-white/90 py-3'
        }`}
        style={{ backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }}
      >
        {/* Top rainbow stripe */}
        <div className="h-1 w-full" style={{
          background: 'linear-gradient(90deg, #FF3B3B, #FF7F00, #F9C513, #3CB044, #1565C0, #7B2FBE)'
        }} />

        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between py-2">

            {/* Logo Only — No Text */}
            <Link to="/" className="flex items-center group flex-shrink-0" aria-label="The Tiny Times - Home">
              <motion.img
                src="/logo.png"
                alt="The Tiny Times"
                className={`object-contain transition-all duration-300 group-hover:scale-105 ${
                  isScrolled ? 'h-12 md:h-14' : 'h-14 md:h-16'
                }`}
                style={{ maxWidth: '220px' }}
                onError={(e) => { e.target.style.display = 'none'; }}
                whileHover={{ scale: 1.03 }}
                transition={{ duration: 0.2 }}
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-1 mx-4">
              {NAV_LINKS.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`nav-link ${isActive(link.path) ? 'active' : ''}`}
                >
                  {link.name}
                </Link>
              ))}
            </nav>

            {/* CTA + Mobile Button */}
            <div className="flex items-center gap-3">
              {/* WhatsApp CTA — Desktop */}
              <motion.a
                href="https://wa.me/919618377750"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.04, y: -1 }}
                whileTap={{ scale: 0.97 }}
                className="hidden lg:inline-flex items-center gap-2 btn-whatsapp text-base px-5 py-2.5"
                aria-label="Subscribe via WhatsApp"
              >
                <FaWhatsapp className="text-xl" />
                <span>Subscribe</span>
              </motion.a>

              {/* Mobile Menu Toggle */}
              <motion.button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden p-2.5 rounded-xl text-primary-green-dark hover:bg-primary-green/10 transition-colors"
                aria-label="Toggle navigation menu"
                whileTap={{ scale: 0.92 }}
              >
                <AnimatePresence mode="wait" initial={false}>
                  <motion.div
                    key={isMobileMenuOpen ? 'close' : 'open'}
                    initial={{ rotate: -90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: 90, opacity: 0 }}
                    transition={{ duration: 0.15 }}
                  >
                    {isMobileMenuOpen ? (
                      <FaTimes className="text-2xl" />
                    ) : (
                      <FaBars className="text-2xl" />
                    )}
                  </motion.div>
                </AnimatePresence>
              </motion.button>
            </div>

          </div>
        </div>
      </motion.header>

      {/* Mobile Drawer */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              onClick={() => setIsMobileMenuOpen(false)}
              className="fixed inset-0 bg-black/40 z-40 lg:hidden"
              style={{ backdropFilter: 'blur(4px)' }}
            />

            {/* Drawer Panel */}
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', stiffness: 320, damping: 32 }}
              className="fixed top-0 right-0 bottom-0 w-80 z-50 lg:hidden overflow-y-auto"
              style={{
                background: 'linear-gradient(160deg, #FAFDF7 0%, #EDF7EE 100%)',
                boxShadow: '-8px 0 40px rgba(0,0,0,0.15)',
              }}
            >
              {/* Drawer Header */}
              <div className="flex items-center justify-between p-5 border-b border-primary-green/10">
                <img
                  src="/logo.png"
                  alt="The Tiny Times"
                  className="h-12 object-contain"
                  style={{ maxWidth: '160px' }}
                />
                <button
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="p-2 rounded-xl text-neutral-gray-dark hover:text-primary-green-dark hover:bg-primary-green/10 transition-colors"
                  aria-label="Close menu"
                >
                  <FaTimes className="text-xl" />
                </button>
              </div>

              {/* Rainbow stripe */}
              <div className="h-1" style={{
                background: 'linear-gradient(90deg, #FF3B3B, #FF7F00, #F9C513, #3CB044, #1565C0, #7B2FBE)'
              }} />

              {/* Nav Links */}
              <nav className="flex flex-col p-5 gap-1">
                {NAV_LINKS.map((link, index) => (
                  <motion.div
                    key={link.path}
                    initial={{ opacity: 0, x: 30 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.06, duration: 0.3 }}
                  >
                    <Link
                      to={link.path}
                      className={`flex items-center gap-3 px-4 py-3.5 rounded-xl font-display font-semibold text-lg transition-all duration-200 ${
                        isActive(link.path)
                          ? 'bg-primary-green text-white shadow-sm'
                          : 'text-neutral-gray-dark hover:bg-primary-green/8 hover:text-primary-green-dark'
                      }`}
                    >
                      <span>{link.icon || '→'}</span>
                      {link.name}
                    </Link>
                  </motion.div>
                ))}
              </nav>

              {/* WhatsApp CTA */}
              <div className="px-5 pb-5">
                <motion.a
                  href="https://wa.me/919618377750"
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.97 }}
                  className="flex items-center justify-center gap-3 w-full py-4 rounded-2xl font-display font-bold text-white text-lg"
                  style={{ background: 'linear-gradient(135deg, #25D366, #128C7E)', boxShadow: '0 4px 20px rgba(37,211,102,0.35)' }}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                >
                  <FaWhatsapp className="text-2xl" />
                  Subscribe via WhatsApp
                </motion.a>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;